import type { FacetableFieldsDTO, FacetsDTO, FacetsService } from '@empathyco/playboard-core-domain'
import type { ComputedRef, Ref } from 'vue'
import type { EpbSelectOption } from '../components/shared/epb-select'
import type { BrowseFacetDTO, RegularFilter } from '../models'
import type { BrowseFacetService } from '../services'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { computed, ref } from 'vue'
import { CONTAINER } from '../config'
import { TYPES } from '../config/inversify.types'
import { FacetTypeEnum } from '../enums/facet-type.enum'
import { FeatureIdEnum } from '../enums/feature-id.enum'
import { FiltersUtils, getFacetsBrowseListParams } from '../utils'
import { useEndpoints } from './use-endpoints'
import { useInstance } from './use-instance'

export function isNonSortableFacetType(type: FacetTypeEnum | string): boolean {
  return (
    type === FacetTypeEnum.RANGE || type === FacetTypeEnum.BOOLEAN || type === FacetTypeEnum.SLIDER
  )
}

export function useFacets(): {
  facetsOptions: Ref<BrowseFacetDTO[]>
  attributeOptions: ComputedRef<Array<EpbSelectOption>>
  searchFacets: (
    item: FacetsDTO,
    pivots: Record<string, RegularFilter>,
    sort?: boolean,
  ) => Promise<void>
  facetableFields: () => Promise<FacetableFieldsDTO>
} {
  const service = CONTAINER.get<BrowseFacetService>(TYPES.BrowseFacetService)

  const facetsService = CONTAINER.get<FacetsService>(TYPES.FacetsService)

  const facetsOptions = ref<BrowseFacetDTO[]>([])

  const { instanceConfig } = useInstance()
  const { endpoints } = useEndpoints()

  const attributeOptions = computed<Array<EpbSelectOption>>(() => {
    return (
      instanceConfig.value.domains[DomainEnum.BROWSE]?.tools[
        FeatureIdEnum.CONFIGURATION
      ]?.attributes.map(attribute => ({
        value: attribute.id,
        label: attribute.prettyName ?? attribute.id,
        isCategory: attribute.isCategory ?? false,
      })) ?? []
    )
  })

  const searchFacets = async (
    item: FacetsDTO,
    pivots: Record<string, RegularFilter>,
    sort?: boolean,
  ): Promise<void> => {
    try {
      const endpointContext = endpoints.value.find(
        endpoint => endpoint.value === item.endpointId,
      )?.context
      const results = await service.list({
        context: item.endpointId ? item.endpointId : DomainEnum.SEARCH,
        ...getFacetsBrowseListParams(item, endpointContext),
        pivots: Object.values(pivots)
          .filter(pivot => pivot.config.mandatory)
          .map(pivot => {
            return {
              name: pivot.config.id,
              values: FiltersUtils.encodeValue(pivot.config, pivot.selectedValues[0])
                ? [FiltersUtils.encodeValue(pivot.config, pivot.selectedValues[0])]
                : [''],
            }
          }),
        facetableFields: sort ? item.config.facetableFields : undefined,
        query: item.criteria.context ? item.criteria.context[0]?.values : [],
      })
      facetsOptions.value = results
    } catch (error) {
      throw error
    }
  }

  const facetableFields = async (): Promise<FacetableFieldsDTO> => {
    try {
      const response = await facetsService.list({
        strictMatching: true,
        endpointId: 'facet-values',
        feature: 'faceting',
      })

      return response.results?.[0].config.facetableFields
    } catch (error) {
      throw error
    }
  }

  return {
    facetsOptions,
    attributeOptions,
    searchFacets,
    facetableFields,
  }
}
